import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooks } from '../context/BookContext';
import { supabase } from '../lib/supabase';
import { Users, BookOpen, Clock, User, Plus, BarChart3, ArrowUpRight, Search, ExternalLink, BookText, GitBranch, Database, Shield, Coffee, Activity, UserPlus } from 'lucide-react';
import ReadingHeatmap from '../components/ReadingHeatmap';
import BookGroupModal from '../components/BookGroupModal';
import GroupHub from '../components/GroupHub';
import Milestones from '../components/Milestones';
import SanctuaryFavorites from "../components/SanctuaryFavorites";
import UserProfileModal from "../components/UserProfileModal";

// Founder reward model isn't finalized yet - keep granting it (so we can tag
// early adopters now) but hide the badge everywhere until launch is decided.
const FOUNDER_BADGE_VISIBLE = false;

// Cozy Steaming Mug SVG Progression Component
const SteamingMug = ({ streak, beverage }) => {
  const mugColors = {
    clay: { body: '#c88f72', rim: '#b5795b', label: 'Earthy Clay Mug' },
    sage: { body: '#a3b899', rim: '#8ca281', label: 'Cozy Sage Ceramic' },
    porcelain: { body: '#fcfcfc', rim: '#f2eae1', flower: '#f06292', label: 'Wildflower Porcelain' },
    sunset: { body: 'url(#sunset-grad)', rim: '#ff7043', label: 'Sunset Tumbler' },
    chalice: { body: '#ffd700', rim: '#ffc107', gem: '#e91e63', label: 'Librarian Chalice' },
  };

  const getMugKey = (count) => {
    if (count <= 2) return 'clay';
    if (count <= 6) return 'sage';
    if (count <= 13) return 'porcelain';
    if (count <= 29) return 'sunset';
    return 'chalice';
  };

  const mugKey = getMugKey(streak);
  const currentMug = mugColors[mugKey];

  return (
    <div style={{ position: 'relative', width: '90px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Steam lines (SVG with rising animations) */}
      <svg width="40" height="40" viewBox="0 0 40 40" style={{ position: 'absolute', top: '5px', left: '25px', zIndex: 5 }}>
        <defs>
          <style>
            {`
              @keyframes steamRise {
                0% { transform: translateY(8px) scaleX(0.9); opacity: 0; }
                15% { opacity: 0.5; }
                50% { transform: translateY(-3px) scaleX(1.2); opacity: 0.25; }
                100% { transform: translateY(-12px) scaleX(0.7); opacity: 0; }
              }
              .steam-path {
                fill: none;
                stroke: var(--text-soft);
                stroke-width: 1.5;
                stroke-linecap: round;
                animation: steamRise 3.5s infinite ease-in-out;
              }
              .steam-1 { animation-delay: 0s; }
              .steam-2 { animation-delay: 1.2s; stroke-dasharray: 3,3; }
              .steam-3 { animation-delay: 2.4s; }
            `}
          </style>
        </defs>
        <path className="steam-path steam-1" d="M15,35 Q10,25 20,15 T15,5" />
        <path className="steam-path steam-2" d="M22,35 Q27,25 17,15 T22,5" />
        <path className="steam-path steam-3" d="M30,35 Q25,25 35,15 T30,5" />
      </svg>

      {/* Mug SVG */}
      <svg width="70" height="60" viewBox="0 0 80 70" style={{ position: 'absolute', bottom: '10px' }}>
        <defs>
          <linearGradient id="sunset-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffb74d" />
            <stop offset="50%" stopColor="#ff7043" />
            <stop offset="100%" stopColor="#9c27b0" />
          </linearGradient>
        </defs>
        
        {/* Coaster (wooden disc) */}
        <ellipse cx="40" cy="58" rx="35" ry="8" fill="#8d6e63" stroke="#5d4037" strokeWidth="1" />
        
        {/* Mug Handle */}
        {mugKey !== 'chalice' ? (
          <path d="M52,25 C66,25 66,45 52,45" fill="none" stroke={mugKey === 'sunset' ? '#ff7043' : currentMug.rim} strokeWidth="5" strokeLinecap="round" />
        ) : (
          <>
            <path d="M20,30 C6,30 6,45 20,45" fill="none" stroke="#ffc107" strokeWidth="3" />
            <path d="M60,30 C74,30 74,45 60,45" fill="none" stroke="#ffc107" strokeWidth="3" />
          </>
        )}

        {/* Mug Body */}
        {mugKey !== 'chalice' ? (
          <path d="M25,20 L55,20 C55,20 53,52 40,52 C27,52 25,20 25,20 Z" fill={currentMug.body} stroke={currentMug.rim} strokeWidth="1" />
        ) : (
          // Chalice body
          <>
            <path d="M25,15 L55,15 L50,40 L30,40 Z" fill="#ffd700" stroke="#ffc107" strokeWidth="1" />
            <rect x="37" y="40" width="6" height="12" fill="#ffd700" stroke="#ffc107" strokeWidth="1" />
            <ellipse cx="40" cy="52" rx="15" ry="4" fill="#ffd700" stroke="#ffc107" strokeWidth="1" />
          </>
        )}

        {/* Custom Mug Details */}
        {mugKey === 'porcelain' && (
          <circle cx="40" cy="36" r="3.5" fill="#f06292" />
        )}
        {mugKey === 'chalice' && (
          <polygon points="40,23 44,27 40,31 36,27" fill="#e91e63" />
        )}

        {/* Beverage Specific Motifs */}
        {beverage === 'tea' ? (
          <>
            {/* Tea bag string dangling from rim over the side */}
            <path d="M 45,20 C 48,25 43,32 46,38" fill="none" stroke="#d7ccc8" strokeWidth="1.2" />
            {/* Tea bag tag */}
            <polygon points="43,38 49,38 49,45 43,45" fill="#c8e6c9" stroke="#81c784" strokeWidth="0.5" />
            <circle cx="46" cy="41.5" r="1" fill="#4caf50" />
          </>
        ) : (
          /* Coffee bean motif on non-porcelain, non-chalice mugs for visual balance */
          mugKey !== 'porcelain' && mugKey !== 'chalice' && (
            <>
              <ellipse cx="40" cy="36" rx="4" ry="2.5" fill="#5d4037" transform="rotate(-30 40 36)" />
              <path d="M 37.5,37.5 C 39,36.5 41,35.5 42.5,34.5" fill="none" stroke="#3e2723" strokeWidth="0.7" />
            </>
          )
        )}
      </svg>
    </div>
  );
};

// Morning Brew check-in Card Component
const MorningBrewCard = () => {
  const { todayBrew, brewLoading, checkInBrew, streakCount, highestStreakCount, brewHistory } = useBooks();
  const [choosingTea, setChoosingTea] = useState(null);
  const [steamActive, setSteamActive] = useState(todayBrew);

  useEffect(() => {
    setSteamActive(todayBrew);
  }, [todayBrew]);

  const handleBrew = async (beverage) => {
    setChoosingTea(beverage);
    setTimeout(async () => {
      await checkInBrew(beverage);
      setChoosingTea(null);
      setSteamActive(true);
    }, 1500);
  };

  const getMugLabel = (count) => {
    if (count <= 2) return 'Clay Mug';
    if (count <= 6) return 'Sage Ceramic';
    if (count <= 13) return 'Wildflower Porcelain';
    if (count <= 29) return 'Sunset Tumbler';
    return 'Grand Librarian Chalice';
  };

  const todayCheckin = brewHistory?.find(h => h.check_in_date === new Date().toISOString().split('T')[0]);
  const beverage = todayCheckin?.mug_style || 'coffee';

  return (
    <div style={brewCardStyle}>
      <div style={brewCardLeft}>
        {steamActive ? (
          <SteamingMug streak={streakCount} beverage={beverage} />
        ) : (
          <div style={emptyCoasterStyle}>
            <svg width="70" height="30" viewBox="0 0 80 40">
              <ellipse cx="40" cy="20" rx="35" ry="8" fill="#8d6e63" stroke="#5d4037" strokeWidth="1" />
            </svg>
          </div>
        )}
      </div>
      <div style={brewCardRight}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <Coffee size={14} color="var(--accent)" />
          <span style={{ fontSize: '0.65rem', fontWeight: 'bold', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Morning Ritual
          </span>
        </div>

        {choosingTea ? (
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 'bold', marginBottom: '2px' }}>
              {choosingTea === 'tea' ? 'Steeping your tea...' : 'Brewing your coffee...'}
            </div>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-soft)' }}>
              {choosingTea === 'tea' ? 'Water is boiling, infusing herbal sanctuary vibes...' : 'Beans are grinding, releasing rich dark focus aromas...'}
            </div>
          </div>
        ) : todayBrew ? (
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 'bold', marginBottom: '2px' }}>
              Sanctuary {beverage === 'tea' ? 'Tea' : 'Coffee'} {getMugLabel(streakCount)} Ready
            </div>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-soft)', marginBottom: '6px' }}>
              Your cozy daily check-in is complete!
            </div>
            <div style={streakBadgeStyle}>
              🔥 <b>{streakCount} Day Streak</b> <span style={{ color: 'var(--text-soft)', fontWeight: 'normal' }}>(Highest: {highestStreakCount})</span>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 'bold', marginBottom: '2px' }}>Brew Your Morning Drink</div>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-soft)', marginBottom: '8px' }}>
              Brew a fresh hot beverage to start your daily reading flow and maintain your streak.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button disabled={brewLoading} onClick={() => handleBrew('tea')} style={brewActionBtnStyle}>🍵 Brew Tea</button>
              <button disabled={brewLoading} onClick={() => handleBrew('coffee')} style={brewActionBtnStyle}>☕ Brew Coffee</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const formatActivityText = (a) => {
  const userDisp = a.username || a.email || 'Someone';
  const timeDisp = new Date(a.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  let actionText = '';
  switch (a.activity_type) {
    case 'signup':
      actionText = 'joined the Sanctuary! 🎉';
      break;
    case 'signin':
      actionText = 'signed in.';
      break;
    case 'book_added':
      actionText = `added "${a.metadata?.title || 'a book'}" by ${a.metadata?.author || 'Unknown'}`;
      break;
    case 'book_completed':
      actionText = `finished "${a.metadata?.title || 'a book'}"! 🏆`;
      break;
    case 'brew_checkin':
      actionText = `brewed their morning ritual (${a.metadata?.mug_style || 'clay'} coaster) ☕`;
      break;
    case 'reading_logged':
      actionText = `read ${a.metadata?.minutes || 0} mins of "${a.metadata?.book_title || 'a book'}"`;
      break;
    case 'group_message':
      actionText = `posted in Guild "${a.metadata?.group_name || 'Group'}" 💬`;
      break;
    default:
      actionText = `interacted (${a.activity_type})`;
  }
  return { text: actionText, user: userDisp, time: timeDisp };
};


const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const [adminStats, setAdminStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Group State
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupHeatmap, setGroupHeatmap] = useState([]);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [peekUserId, setPeekUserId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [savingUserId, setSavingUserId] = useState(null);

  const navigate = useNavigate();
  const { books, logs, loading, clearLibrary, todayBrew, brewLoading, checkInBrew, streakCount, highestStreakCount } = useBooks();

  const readCount = books.filter(b => b.status === 'read').length;
  const tbrCount = books.filter(b => b.status === 'tbr').length;

  useEffect(() => {
    fetchUserGroups();
    checkAdminStatus();
    fetchAllUsers();
  }, []);

  const checkAdminStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user && user.email === 'howelldanr@gmail.com') {
      setIsAdmin(true);
      fetchAdminStats();
    }
  };

  const fetchAdminStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_admin_analytics');
      if (error) {
        console.error("Admin RPC Error:", error.message, error.details, error.hint);
        return;
      }
      setAdminStats(data);
    } catch (err) {
      console.error("Admin fetch failed", err);
    }
  };

  const fetchAllUsers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, username, avatar_url, role, is_founder, is_system_admin')
      .order('username', { ascending: true });
    setAllUsers(data || []);
  };

  const handleUpdateUser = async (userId, role, isFounder) => {
    setSavingUserId(userId);
    const { error } = await supabase.rpc('admin_update_user_role', {
      target_user_id: userId,
      new_role: role,
      new_is_founder: isFounder,
    });
    if (!error) {
      setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, role, is_founder: isFounder } : u));
    } else {
      alert('Update failed: ' + error.message);
    }
    setSavingUserId(null);
  };

  const fetchUserGroups = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('book_groups')
      .select('*, group_members!inner(role, user_id)')
      .eq('group_members.user_id', user.id)
      .eq('status', 'active');
    
    if (data) {
      setGroups(data);
    }
  };

  const handleGroupSelect = async (groupId) => {
    setSelectedGroupId(groupId);
    const { data, error } = await supabase.rpc('get_group_heatmap', { target_group_id: groupId });
    if (!error && data) setGroupHeatmap(data);
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: '3rem', color: '#999' }}>Loading Sanctuary...</div>;

  return (
    <div>

      {/* Tab Switcher */}
      <div style={tabContainer}>
        <button onClick={() => setActiveTab('personal')} style={activeTab === 'personal' ? activeTabBtn : tabBtn}>
          <User size={18} /> Me
        </button>
        <button onClick={() => setActiveTab('groups')} style={activeTab === 'groups' ? activeTabBtn : tabBtn}>
          <Users size={18} /> Groups
        </button>
        {isAdmin && (
          <button onClick={() => setActiveTab('admin')} style={activeTab === 'admin' ? activeTabBtn : tabBtn}>
            <BarChart3 size={18} /> Admin
          </button>
        )}
      </div>

      {/* PERSONAL TAB */}
      {activeTab === 'personal' && (
        <div className="fade-in">
          <MorningBrewCard />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={statCardStyle}>
              <h3 style={{ margin: 0, fontSize: '1.8rem', color: 'var(--accent)' }}>{readCount}</h3>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-soft)', fontWeight: 'bold' }}>BOOKS READ</span>
            </div>
            <div style={statCardStyle}>
              <h3 style={{ margin: 0, fontSize: '1.8rem', color: '#ff9800' }}>{tbrCount}</h3>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-soft)', fontWeight: 'bold' }}>ON TBR</span>
            </div>
          </div>
          <ReadingHeatmap logs={logs} title="My Growth" />
          <button onClick={() => navigate('/journal')} style={logSessionBtnStyle}>
            <Clock size={16} /> Log a reading session
          </button>

          {books.length === 0 ? (
            <div style={emptyStateStyle}>
              <p>Your sanctuary is quiet. Start by adding a book!</p>
              <button onClick={() => navigate('/library')} style={primaryButtonStyle}>Add your first book</button>
            </div>
          ) : (
            <Milestones />
          )}
        </div>
      )}

      {/* GROUPS TAB */}
      {activeTab === 'groups' && (
        <div className="fade-in">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-main)' }}>My Reading Circles</h3>
            <button 
              onClick={() => setIsGroupModalOpen(true)}
              style={subBtnStyle}
            >
              <Plus size={16} /> Join / Create
            </button>
          </div>

          {groups.length === 0 ? (
            <div style={emptyStateStyle}>
              <Users size={48} color="var(--border)" style={{ marginBottom: '1rem' }} />
              <p>Community is better with friends. Join or create a group to track progress together!</p>
              <button onClick={() => setIsGroupModalOpen(true)} style={primaryButtonStyle}>Get Started</button>
            </div>
          ) : selectedGroupId ? (
            <GroupHub groupId={selectedGroupId} onBack={() => setSelectedGroupId(null)} />
          ) : (
            <div style={groupsGrid}>
              {groups.map(g => {
                const myMemberInfo = g.group_members?.[0] || {};
                const myRole = myMemberInfo.role || 'member';
                const customRoleName = g.role_names?.[myRole] || myRole;

                return (
                  <div 
                    key={g.id} 
                    onClick={() => handleGroupSelect(g.id)} 
                    style={richGroupCard} 
                    className="hover-card"
                  >
                    <div style={richGroupCardHeader}>
                      <div style={groupIconWrapper}>
                        <Users size={20} color="var(--accent)" />
                      </div>
                      <span style={richGroupBadge}>{customRoleName}</span>
                    </div>
                    
                    <h4 style={richGroupTitle}>{g.name}</h4>
                    <p style={richGroupDesc}>{g.description || 'Welcome to this cozy reading sanctuary.'}</p>
                    
                    <div style={richGroupFooter}>
                      <button style={enterGroupBtn}>
                        Enter Sanctuary
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ADMIN TAB */}
      {activeTab === 'admin' && isAdmin && (
        <div className="fade-in">
          <div style={sectionContainer}>
            <div style={sectionHeader}>
              <BarChart3 size={20} color="var(--accent)" />
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-main)' }}>Admin Intelligence</h3>
            </div>

            {!adminStats ? (
              <p style={{ fontSize: '0.9rem', color: 'var(--text-soft)' }}>Gathering intelligence...</p>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
                  <div style={adminCard}>
                    <Users size={16} color="#4caf50" />
                    <div style={adminVal}>{adminStats.total_users}</div>
                    <div style={adminLabel}>Total Users</div>
                  </div>
                  <div style={adminCard}>
                    <Clock size={16} color="#2196f3" />
                    <div style={adminVal}>{adminStats.active_users_30d}</div>
                    <div style={adminLabel}>Active (30d)</div>
                  </div>
                  <div style={adminCard}>
                    <BookOpen size={16} color="#9c27b0" />
                    <div style={adminVal}>{adminStats.total_books_stored}</div>
                    <div style={adminLabel}>Books Saved</div>
                  </div>
                  <div style={adminCard}>
                    <Search size={16} color="#ff9800" />
                    <div style={adminVal}>{adminStats.cache_hits}</div>
                    <div style={adminLabel}>Global Cache</div>
                  </div>
                  <div style={adminCard}>
                    <Activity size={16} color="#e91e63" />
                    <div style={adminVal}>{adminStats.active_today || 0}</div>
                    <div style={adminLabel}>Active Today</div>
                  </div>
                  <div style={adminCard}>
                    <UserPlus size={16} color="#00bcd4" />
                    <div style={adminVal}>
                      {adminStats.signups_today || 0}
                      <span style={{ fontSize: '0.65rem', fontWeight: 'normal', color: 'var(--text-soft)', marginLeft: '4px' }}>
                        ({adminStats.signins_today || 0} in)
                      </span>
                    </div>
                    <div style={adminLabel}>Signups Today</div>
                  </div>
                </div>

                <h4 style={{ fontSize: '0.7rem', color: 'var(--text-soft)', marginBottom: '10px', textTransform: 'uppercase' }}>Recent Sanctuary Activity</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {adminStats.recent_activity && adminStats.recent_activity.length > 0 ? (
                    adminStats.recent_activity.slice(0, 15).map((a, i) => {
                      const { text, user, time } = formatActivityText(a);
                      return (
                        <div key={i} style={activityRow}>
                          <div style={{ overflow: 'hidden', flex: 1 }}>
                            <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--accent)' }}>{user} </span>
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-main)' }}>{text}</span>
                            <div style={{ fontSize: '0.65rem', color: 'var(--text-soft)', marginTop: '2px' }}>{time}</div>
                          </div>
                          <ArrowUpRight size={14} color="var(--text-soft)" style={{ flexShrink: 0, marginLeft: '8px' }} />
                        </div>
                      );
                    })
                  ) : (
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-soft)', margin: 0 }}>No recent activity logged yet.</p>
                  )}
                </div>

                <h4 style={{ fontSize: '0.7rem', color: 'var(--text-soft)', margin: '24px 0 10px 0', textTransform: 'uppercase' }}>Hosted Services</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {[
                    { label: 'Wiki', href: 'https://collab.leafandletter.site', icon: <BookText size={16} /> },
                    { label: 'Git', href: 'https://git.leafandletter.site', icon: <GitBranch size={16} /> },
                    { label: 'DB Studio', href: 'https://db.leafandletter.site', icon: <Database size={16} /> },
                    { label: 'Authentik', href: 'https://auth.leafandletter.site', icon: <Shield size={16} /> }
                  ].map(svc => (
                    <a key={svc.label} href={svc.href} target="_blank" rel="noopener noreferrer" style={hostedLinkStyle}>
                      {svc.icon}
                      <span style={{ flex: 1, fontWeight: 'bold', fontSize: '0.85rem' }}>{svc.label}</span>
                      <ExternalLink size={12} color="var(--text-soft)" />
                    </a>
                  ))}
                </div>

                {/* User Management */}
                <h4 style={{ fontSize: '0.7rem', color: 'var(--text-soft)', margin: '24px 0 10px 0', textTransform: 'uppercase' }}>User Management</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {allUsers.map(u => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: 'var(--bg-app)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{u.avatar_url || '📚'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-main)' }}>{u.username || 'Anonymous'}</span>
                          {FOUNDER_BADGE_VISIBLE && u.is_founder && <span style={{ fontSize: '0.65rem', backgroundColor: '#ffd700', color: '#5a3e00', fontWeight: 'bold', padding: '1px 6px', borderRadius: '8px' }}>🌿 Founder</span>}
                          {u.role === 'employee' && <span style={{ fontSize: '0.65rem', backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', padding: '1px 6px', borderRadius: '8px' }}>👔 Employee</span>}
                          {u.role === 'partner' && <span style={{ fontSize: '0.65rem', backgroundColor: '#f3e5f5', color: '#6a1b9a', fontWeight: 'bold', padding: '1px 6px', borderRadius: '8px' }}>🤝 Partner</span>}
                          {u.is_system_admin && <span style={{ fontSize: '0.65rem', backgroundColor: '#ffebee', color: '#b71c1c', fontWeight: 'bold', padding: '1px 6px', borderRadius: '8px' }}>🛡 Admin</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <select
                          value={u.role}
                          disabled={savingUserId === u.id}
                          onChange={e => handleUpdateUser(u.id, e.target.value, u.is_founder)}
                          style={{ fontSize: '0.75rem', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-card)', color: 'var(--text-main)', cursor: 'pointer' }}
                        >
                          <option value="public">Public</option>
                          <option value="employee">Employee</option>
                          <option value="partner">Partner</option>
                        </select>
                        <button
                          onClick={() => handleUpdateUser(u.id, u.role, !u.is_founder)}
                          disabled={savingUserId === u.id}
                          style={{ fontSize: '0.7rem', padding: '4px 8px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: u.is_founder ? '#ffd700' : 'var(--bg-card)', color: u.is_founder ? '#5a3e00' : 'var(--text-soft)', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          {savingUserId === u.id ? '…' : u.is_founder ? '🌿 Founder' : 'Grant Founder'}
                        </button>
                      </div>
                    </div>
                  ))}
                  {allUsers.length === 0 && <p style={{ fontSize: '0.8rem', color: 'var(--text-soft)', margin: 0 }}>No users found.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isGroupModalOpen && <BookGroupModal myGroups={groups} onClose={() => setIsGroupModalOpen(false)} onGroupAction={fetchUserGroups} />}
      {peekUserId && <UserProfileModal userId={peekUserId} onClose={() => setPeekUserId(null)} />}
    </div>
  );
};

// Styles
const tabContainer = {
  display: 'flex', gap: '10px', marginBottom: '2rem', padding: '4px',
  backgroundColor: 'var(--bg-app)', borderRadius: '12px', border: '1px solid var(--border)'
};

const tabBtn = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  padding: '10px', borderRadius: '8px', border: 'none', background: 'none',
  color: 'var(--text-soft)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold',
  transition: 'all 0.2s'
};

const activeTabBtn = {
  ...tabBtn, backgroundColor: 'var(--bg-card)', color: 'var(--accent)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
};

const statCardStyle = {
  backgroundColor: 'var(--bg-card)', padding: '1.5rem', borderRadius: '16px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', border: '1px solid var(--border)'
};

const sectionContainer = {
  backgroundColor: 'var(--bg-card)', padding: '1.5rem', borderRadius: '20px',
  border: '1px solid var(--border)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
};

const sectionHeader = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' };

const groupsGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginTop: '10px' };
const richGroupCard = { display: 'flex', flexDirection: 'column', padding: '20px', borderRadius: '20px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-card)', color: 'var(--text-main)', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' };
const richGroupCardHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' };
const groupIconWrapper = { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const richGroupBadge = { fontSize: '0.7rem', padding: '4px 8px', borderRadius: '10px', backgroundColor: 'var(--bg-app)', border: '1px solid var(--border)', fontWeight: 'bold', color: 'var(--text-soft)' };
const richGroupTitle = { margin: '0 0 10px 0', fontSize: '1.1rem', color: 'var(--text-main)', fontWeight: 'bold' };
const richGroupDesc = { fontSize: '0.8rem', color: 'var(--text-soft)', lineHeight: '1.4', flex: '1', marginBottom: '20px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '50px' };
const richGroupFooter = { display: 'flex', justifyContent: 'flex-end' };
const enterGroupBtn = { padding: '8px 16px', borderRadius: '10px', border: 'none', backgroundColor: 'var(--accent)', color: 'white', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem', transition: 'opacity 0.2s' };
const subBtnStyle = { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold', backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' };

const adminCard = { backgroundColor: 'var(--bg-app)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)' };
const adminVal = { fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-main)', margin: '4px 0' };
const adminLabel = { fontSize: '0.55rem', color: 'var(--text-soft)', fontWeight: 'bold', textTransform: 'uppercase' };
const activityRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: 'var(--bg-app)', borderRadius: '10px', border: '1px solid var(--border)' };
const hostedLinkStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: 'var(--bg-app)', borderRadius: '10px', border: '1px solid var(--border)', textDecoration: 'none', color: 'var(--text-main)' };

const logSessionBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%',
  padding: '12px', margin: '1rem 0 1.5rem 0', borderRadius: '12px', border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-card)', color: 'var(--accent)', fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer'
};

const emptyStateStyle = {
  padding: '40px', backgroundColor: 'var(--bg-app)', borderRadius: '16px',
  border: '2px dashed var(--border)', textAlign: 'center', marginTop: '20px', color: 'var(--text-soft)'
};

const primaryButtonStyle = {
  backgroundColor: 'var(--accent)', color: 'white', border: 'none',
  padding: '12px 24px', borderRadius: '12px', cursor: 'pointer', fontSize: '1rem', fontWeight: 'bold', marginTop: '15px'
};

// Morning Brew Ritual Custom Styles
const brewCardStyle = {
  display: 'flex',
  backgroundColor: 'var(--bg-card)',
  borderRadius: '20px',
  border: '1px solid var(--border)',
  padding: '1rem',
  marginBottom: '1.5rem',
  boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
  alignItems: 'center',
  gap: '15px'
};

const brewCardLeft = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const brewCardRight = {
  flex: 1
};

const emptyCoasterStyle = {
  width: '90px',
  height: '90px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.5
};

const streakBadgeStyle = {
  display: 'inline-block',
  padding: '3px 8px',
  borderRadius: '12px',
  backgroundColor: 'var(--bg-app)',
  border: '1px solid var(--border)',
  fontSize: '0.65rem',
  color: 'var(--text-main)'
};

const brewActionBtnStyle = {
  padding: '5px 10px',
  borderRadius: '8px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-main)',
  cursor: 'pointer',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  transition: 'all 0.2s'
};

export default Dashboard;